import { call, put, select, take } from "redux-saga/effects";
import { httpGet, httpPost, httpPut } from "../../services";
import { handleLocalStorageClear, setCurrentAppInfo } from "../../utils";
import { APIErrorResponseSchema } from "../actions";
import { initAppSuccessAction, setSamlConfigStatus, setTrialPlanFlow, showLoadingAction } from "../actions/appStateActions";
import {
  AppBasicInfoSchema,
  AppVersionSchema,
  AppViewPermissionSchema,
  AuthConfigSchema,
  AuthConst,
  AuthLoginPayloadSchema,
  AuthLoginSchema,
  AuthValidateTokenSchema,
  getAllSitesErrorAction,
  getAllSitesLoadingAction,
  GetAllSitesSchema,
  getAllSitesSuccessAction,
  getAppFeatureAction,
  getAppFeatureErrorAction,
  getAppFeatureLoadingAction,
  getAppFeatureSuccessAction,
  getAppUsersSuccessAction,
  getAuthAppUsersErrorAction,
  getAuthAppUsersLoadingAction,
  GetAuthAppUsersSchema,
  getIntercomUserDataAction,
  getIntercomUserDataErrorAction,
  getIntercomUserDataLoadingAction,
  getIntercomUserDataSuccessAction,
  getSharedSitesAction,
  getSharedSitesErrorAction,
  getSharedSitesLoadingAction,
  getSharedSitesSuccessAction,
  getYourSitesAction,
  getYourSitesErrorAction,
  getYourSitesLoadingAction,
  GetYourSitesSchema,
  getYourSitesSuccessAction,
  initAppBasicInfoAction,
  initAppBasicInfoErrorAction,
  initAppBasicInfoLoadingAction,
  initAppBasicInfoSuccessAction,
  initAppVersionErrorAction,
  initAppVersionLoadingAction,
  initAppVersionSuccessAction,
  initAppViewPermissionErrorAction,
  initAppViewPermissionLoadingAction,
  initAppViewPermissionSuccessAction,
  initAuthConfigErrorAction,
  initAuthConfigLoadingAction,
  initAuthConfigSuccessAction,
  initAuthLoginErrorAction,
  initAuthLoginLoadingAction,
  initAuthLoginSuccessAction,
  initAuthValidateTokenAction,
  initAuthValidateTokenLoadingAction,
  initSearchJsonSuccessAction,
  setCurrentAppInfoSuccessAction,
  TokenLifetimeSchema,
  updateAppBasicInfoErrorAction,
  updateAppBasicInfoLoadingAction,
  updateAppBasicInfoSuccessAction,
  updateTokenLifetimeErrorAction,
  updateTokenLifetimeLoadingAction,
  updateTokenLifetimeSuccessAction
} from "../actions/authActions";
import { getWebAppsAction } from "../actions/deploymentActions";
import { getMultipleSocialProviderAction } from "../actions/platformConfigurationActions";

import { getDataCenterAction } from "../actions/dataConfigurationActions";
import { getTrialPlanLimitsError, getTrialPlanLimitsRequest, getTrialPlanLimitsSuccess } from "../features/trialLimit/trialLimit";
import { onboardingFailure, onboardingRequest, onboardingSuccess, OnboardingPayload, OnboardingResult } from "../features/onboarding/onboarding";
import { RootSchema } from "../reducers";
import { trackEvent } from "../../services/analytics/tracking";
import { AnalyticsEvent } from "../../services/analytics/types";
const authConfigLoad = true;
/**
 * Handlers
 */

/*
 * Auth Config Handler
 */
function* getPermissionHandler() {
  try {
    yield put(initAppViewPermissionLoadingAction());
    const data = yield httpGet("/auth/permissions");
    const { permissions } = data;
    yield put(initAppViewPermissionSuccessAction(permissions as AppViewPermissionSchema));
  } catch (e: any) {
    yield put(initAppViewPermissionErrorAction(e.message));
  }
}

function* authConfigHandler() {
  try {
    const havetoken = localStorage.getItem("isLoggedIn") === "true";
    if (havetoken && location.pathname === "/logout") {
      handleLocalStorageClear();
    }
    yield put(showLoadingAction(true, "Please wait..."));
    const params = new URLSearchParams(window.location.search);
    const samlconfig = params.get("samlconfig");
    if (!havetoken && samlconfig) {
      try {
        const samlResp = yield httpGet("/auth/checksaml", { sitename: `saml_${samlconfig}` });
        yield put(setSamlConfigStatus(samlResp.isFound));
      } catch (error) {
        yield put(setSamlConfigStatus(undefined));
      }
    }
    yield put(initAuthConfigLoadingAction());
    const configData: AuthConfigSchema = yield httpGet("/auth/config");
    yield put(initAuthConfigSuccessAction(configData));
    yield put(initAppSuccessAction());
    // Non-logged-in users will be redirected to /login by route guards.
    // The Login component handles OIDC PKCE initiation and callback.

    //User loggedin and have Appid then call basic setting route to get basic features of app
    if (havetoken && !configData.currentAppId) {
      // User is authenticated but has no app — enter trial/signup flow
      yield put(setTrialPlanFlow(true));
    } else if (havetoken && configData.currentAppId) {
      //Get Intercom user
      yield put(getIntercomUserDataAction());
      //Get Search Json File

      yield call(getSearchJsonData);
      //Get Basic Settings
      if (authConfigLoad) {
        yield put(initAppBasicInfoAction());
        //Get Own Sites
        yield put(getYourSitesAction());
        //Get Shared Sites
        yield put(getSharedSitesAction());
        //Get App Feature
        yield put(getAppFeatureAction());
        //Get Current App Credentials
        yield put(getWebAppsAction());
        yield put(getMultipleSocialProviderAction());
        yield put(getDataCenterAction());
      }
      // Validate Token
      if (!configData.IOLoginUrl && location.pathname !== "/login" && location.pathname !== "/logout") {
        yield put(initAuthValidateTokenAction());
      }
    }
  } catch (e: any) {
    yield put(initAuthConfigErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}
/*
 * Shared post-auth success logic
 */
function* handleAuthSuccess(data: AuthLoginSchema) {
  yield put(setTrialPlanFlow(false));
  yield put(initAppBasicInfoAction());
  localStorage.setItem("isLoggedIn", "true");
  const name = data.app_name || "";
  setCurrentAppInfo(name);
  yield put(initAuthLoginSuccessAction(data));
  yield call(authConfigHandler);
}

const buildLoginActivityEvent = (data: AuthLoginSchema): AnalyticsEvent => ({
  name: "login_activity",
  properties: {
    operation: data.FreshLogin ? "registration" : "login",
    ...(data.RegistrationSource ? { provider_type: data.RegistrationSource } : {}),
    app_id: data.app_id,
    app_name: data.app_name
  }
});

/*
 * Auth Login Handler (OIDC callback)
 */
function* authLoginHandler(payload: AuthLoginPayloadSchema) {
  try {
    yield put(showLoadingAction(true, "Logging in..."));
    yield put(initAuthLoginLoadingAction());
    localStorage.removeItem("isLoggedIn");

    const data: AuthLoginSchema = yield httpPost(
      "/auth/oidc/callback",
      {},
      {
        code: payload.code,
        code_verifier: payload.code_verifier,
        redirect_uri: payload.redirect_uri
      },
      false // Don't send auth headers for login
    );

    if (data.isTrialFlow) {
      yield call(trackEvent, buildLoginActivityEvent(data));
      yield put(setTrialPlanFlow(true));
      localStorage.setItem("isLoggedIn", "true");
      yield put(initAuthLoginSuccessAction(data));
      yield put(showLoadingAction(false));
      return;
    }

    if (!data.authenticated || data.errorCode || data.ErrorCode) {
      const message = data.message || data.Message || "";
      throw new Error(message);
    }

    yield call(trackEvent, buildLoginActivityEvent(data));
    yield call(handleAuthSuccess, data);
  } catch (e: any) {
    yield put(initAuthLoginErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

/*
 * Onboarding Handler (tenant setup)
 */
function* onboardingHandler(payload: OnboardingPayload) {
  try {
    localStorage.removeItem("isLoggedIn");
    const data: OnboardingResult = yield httpPost(
      "/auth/onboard-tenant",
      {},
      {
        appname: payload.appname,
        tenantType: payload.tenantType
      }
    );
    if (data.ErrorCode) {
      yield put(onboardingFailure(data.Message ?? ""));
      return;
    }
    yield put(onboardingSuccess());
    yield call(handleAuthSuccess, data as AuthLoginSchema);
  } catch (e: any) {
    yield put(onboardingFailure(e.message));
  }
}
/*
 * App Basic settings Handler
 */

function* appBasicInfoHandler(noRefresh?: boolean) {
  try {
    if (!noRefresh) yield put(showLoadingAction(true, "Please Wait..."));
    yield put(initAppBasicInfoLoadingAction());
    const infoData = yield httpGet("/auth/basicsettings?d=" + Date.now());
    yield put(initAppBasicInfoSuccessAction(infoData));
  } catch (e: any) {
    yield put(initAppBasicInfoErrorAction(e.message));
  } finally {
    //yield put(showLoadingAction(false));
  }
}

function* getYourSitesHandler() {
  try {
    yield put(getYourSitesLoadingAction());
    const data = yield httpGet("/auth/apps");
    const response = data.Data ? data.Data : [];
    yield put(getYourSitesSuccessAction({ response: response }));
    if (response.length > 0) {
      const siteName = localStorage.getItem("siteName");
      for (let i = 0; i < response.length; i++) {
        if (response[i].AppName === siteName) {
          yield put(setCurrentAppInfoSuccessAction(response[i]));
          break;
        }
      }
    }
  } catch (e: any) {
    yield put(getYourSitesErrorAction(e.message));
  }
}

function* getSharedSitesHandler() {
  try {
    yield put(getSharedSitesLoadingAction());
    const data = yield httpGet("/auth/sharedapps");
    const response = data.Data ? data.Data : [];
    if (response && response.length > 0) {
      const siteName = localStorage.getItem("siteName");
      for (let i = 0; i < response.length; i++) {
        if (response[i].AppName === siteName) {
          response[i].Key = response[i].ApiKey;
          yield put(setCurrentAppInfoSuccessAction(response[i]));
          break;
        }
      }
    } else {
      // get currentAppinfo in store
      const yourSites: GetYourSitesSchema = yield select((state: RootSchema) => state.auth.yourSites);
      const currentAppinfo = yield select((state: RootSchema) => state.auth.currentAppinfo);
      if (!currentAppinfo.AppName && yourSites.isSuccess) {
        if (yourSites.response && yourSites.response.length) {
          localStorage.setItem("siteName", yourSites.response[0].AppName);
          location.reload();
        } else {
          location.href = "/logout";
        }
      }
    }
    yield put(getSharedSitesSuccessAction({ response: response }));
  } catch (e: any) {
    yield put(getSharedSitesErrorAction(e.message));
  }
}

export function* initAppVersionHandler() {
  try {
    yield put(initAppVersionLoadingAction());
    const data: AppVersionSchema = yield httpGet("/auth/version");
    yield put(initAppVersionSuccessAction({ ...data }));
  } catch (e: any) {
    yield put(initAppVersionErrorAction(e.message));
  }
}

export function* initAuthValidateTokenHandler() {
  try {
    yield put(initAuthValidateTokenLoadingAction());
    const data: AuthValidateTokenSchema = yield httpGet("/auth/validatetoken");
    if (data.errorCode) {
      handleLocalStorageClear();
      location.href = "/logout";
    }
  } catch (e: any) {}
}

function* updateAppBasicInfo({ data, noReload }) {
  try {
    yield put(updateAppBasicInfoLoadingAction());
    const response: AppBasicInfoSchema = yield httpPost("/auth/submitbasic", {}, data);

    const basicInfo = yield select((state: RootSchema) => state.auth.appBasicInfo);
    yield put(updateAppBasicInfoSuccessAction(response));
    if (!noReload) {
      yield put(initAppBasicInfoSuccessAction({ ...basicInfo, ...response }));
    }
  } catch (e: any) {
    yield put(updateAppBasicInfoErrorAction(e.message));
  } finally {
    yield put(showLoadingAction(false));
  }
}

function* getIntercomUserDataHandler() {
  if (localStorage.getItem("isLoggedIn") === "true") {
    try {
      yield put(getIntercomUserDataLoadingAction());

      const response = yield httpGet("/auth/intercom/userdata", {
        getLastLoginActivity: true
      });

      yield put(getIntercomUserDataSuccessAction({ response }));
    } catch (e: any) {
      yield put(getIntercomUserDataErrorAction(e.message));
    }
  }
}

function* getAppUsersHandler() {
  try {
    yield put(getAuthAppUsersLoadingAction());

    const response: GetAuthAppUsersSchema = yield httpGet("/auth/appusers");

    if (response.message) {
      throw new Error(response.message);
    }

    yield put(getAppUsersSuccessAction(response));
  } catch (e: any) {
    yield put(getAuthAppUsersErrorAction(e.message));
  }
}

function* getAllSitesHandler() {
  try {
    yield put(getAllSitesLoadingAction());

    const response: GetAllSitesSchema["data"] & APIErrorResponseSchema = yield httpGet("/auth/allapps");

    if (response.message) {
      throw new Error(response.message);
    }

    yield put(getAllSitesSuccessAction({ data: response }));
  } catch (e: any) {
    yield put(getAllSitesErrorAction(e.message));
  }
}

function* getSearchJsonData() {
  const searchJson = window.getNewSearchJSON();
  yield put(initSearchJsonSuccessAction({ data: searchJson }));
}

function* updateTokenLifetimeHandler(payload: TokenLifetimeSchema) {
  try {
    yield put(updateTokenLifetimeLoadingAction());

    const response = yield httpPut("/auth/tokenlifetime", undefined, {
      ...payload
    });
    if (response.ErrorCode) {
      throw new Error(response.Message || response.Description);
    }
    if (response.errorCode) {
      throw new Error(response.message || response.description);
    }

    yield put(updateTokenLifetimeSuccessAction(response));
  } catch (e) {
    yield put(updateTokenLifetimeErrorAction(e.message));
  }
}

function* getTrialLimitsHandler(payload) {
  try {
    const { isB2B } = payload;
    // yield put(putPassKeyConfigLoadingAction());
    // yield put(updatePasskeyLoading());
    const response: any = yield httpGet(`/auth/trial-limits`, {
      limitType: isB2B ? "b2b" : "b2c"
    });
    if (response.ErrorCode || response.errorCode) {
      throw new Error(response.Description || response.Message);
    }
    // yield put(putPassKeyConfigSuccessAction(response));
    yield put(getTrialPlanLimitsSuccess(response));
  } catch (e) {
    // yield put(putPassKeyConfigErrorAction(e.message));
    yield put(getTrialPlanLimitsError(e.message));
  }
}
/**
 * Watchers
 */

export function* watchGetTrialPlanLimits() {
  while (true) {
    const { payload } = yield take(getTrialPlanLimitsRequest({}).type);
    yield call(getTrialLimitsHandler, payload);
  }
}
export function* watchAuthConfig() {
  while (true) {
    yield take(AuthConst.INIT_AUTH_CONFIG);
    yield call(authConfigHandler);
  }
}

export function* watchAuthLogin() {
  while (true) {
    const { payload } = yield take(AuthConst.INIT_AUTH_LOGIN);
    yield call(authLoginHandler, payload);
  }
}

export function* watchOnboarding() {
  while (true) {
    const { payload } = yield take(onboardingRequest.type);
    yield call(onboardingHandler, payload);
  }
}

export function* watchGetYourSites() {
  while (true) {
    yield take(AuthConst.GET_YOUR_SITES);
    yield call(getYourSitesHandler);
  }
}

export function* watchGetSharedSites() {
  while (true) {
    yield take(AuthConst.GET_SHARED_SITES);
    yield call(getSharedSitesHandler);
  }
}

export function* watchAppBasicInfo() {
  while (true) {
    const { payload } = yield take(AuthConst.INIT_APP_BASIC_INFO);
    yield call(appBasicInfoHandler, { ...payload });
  }
}

export function* watchAppVersion() {
  while (true) {
    yield take(AuthConst.INIT_APP_VERSION);
    yield call(initAppVersionHandler);
  }
}

export function* watchAuthValidateToken() {
  while (true) {
    yield take(AuthConst.INIT_AUTH_VALIDATE_TOKEN);
    yield call(initAuthValidateTokenHandler);
  }
}

export function* watchGetPermission() {
  while (true) {
    yield take(AuthConst.INIT_APP_VIEW_PERMISSION);
    yield call(getPermissionHandler);
  }
}
function* getAppFeatureHandler() {
  try {
    yield put(getAppFeatureLoadingAction());
    const data = yield httpGet("/auth/features");
    const features: string[] = [];
    data.Data.map((feature) => {
      features[feature.feature] = feature.status;
    });
    yield put(getAppFeatureSuccessAction(features));
  } catch (e: any) {
    yield put(getAppFeatureErrorAction(e.message));
  }
}
export function* watchGetAppFeature() {
  while (true) {
    yield take(AuthConst.GET_APP_FEATURE);
    yield call(getAppFeatureHandler);
  }
}

export function* watchGetIntercomUserData() {
  while (true) {
    yield take(AuthConst.GET_INTERCOM_USER_DATA);
    yield call(getIntercomUserDataHandler);
  }
}

export function* watchGetAuthAppUsers() {
  while (true) {
    yield take(AuthConst.GET_AUTH_APP_USERS);
    yield call(getAppUsersHandler);
  }
}

export function* watchUpdateAppBasicInfo() {
  while (true) {
    const { payload } = yield take(AuthConst.UPDATE_APP_BASIC_INFO);
    yield call(updateAppBasicInfo, payload);
  }
}

export function* watchGetAllSites() {
  while (true) {
    yield take(AuthConst.GET_ALL_SITES);
    yield call(getAllSitesHandler);
  }
}

export function* watchUpdateTokenLifetime() {
  while (true) {
    const { payload: payload } = yield take(AuthConst.UPDATE_TOKEN_LIFETIME);
    yield call(updateTokenLifetimeHandler, payload);
  }
}
